import { ISlOption, TedivoForm, translateTedivoForm } from "@tedivo/tedivo-form";
import { TAddRemoveCgs, canAddArtificialCgs, createCgsOptions } from "./cgsExportOptions";

import { getTranslation } from "../../../../../../../app/i18/i18tn";
import { z } from "zod";

export interface IExportNsdOptions {
  heightFactor: number;
  showDebug: boolean;
  addRemoveCgs: TAddRemoveCgs;
}

/**
 * VCG height factors offered for the NSD export.
 *
 * 0.0 stores bottom bases directly in the NSD instead of mid-container VCGs, and
 * is the default because that is what NSD consumers normally expect. The other
 * two are the usual mid-container factors.
 */
const HEIGHT_FACTOR_OPTIONS: ISlOption[] = [
  { value: 0, label: "0.0" },
  { value: 0.45, label: "0.45" },
  { value: 0.5, label: "0.5" },
];

const FormValidator: z.Schema<IExportNsdOptions> = z.object({
  // sl-select always yields a string, so the chosen factor is coerced back.
  heightFactor: z.custom<number>().transform((value) => Number(value)),
  showDebug: z.boolean(),
  addRemoveCgs: z.enum(["none", "remove", "addArtificial"]),
});

/**
 * Asks for the options needed to export the current OVD as an NSD file.
 *
 * The shipClass token is not asked for: the converter defaults it to the OVD's
 * own shipClass, which is what we want.
 *
 * @returns the chosen options, or `undefined` if the user cancelled the dialog.
 */
export function createNsdExportOptionsDialog(): Promise<IExportNsdOptions | undefined> {
  return new Promise((resolve) => {
    const withArtificialCgs = canAddArtificialCgs();

    const dialog = document.createElement("sl-dialog");
    dialog.label = getTranslation("view:download.downloadNSDOptions");

    const btnGo = document.createElement("sl-button");
    btnGo.innerHTML = getTranslation("general:common.export");
    btnGo.slot = "footer";
    btnGo.variant = "primary";
    btnGo.type = "submit";

    const tedivoForm = new TedivoForm<IExportNsdOptions>({
      fields: [
        {
          name: "addRemoveCgs",
          type: "select",
          label: getTranslation("general:exportToStaf.cgsOptions"),
          options: createCgsOptions(withArtificialCgs),
          initialValue: "none",
        },
        {
          name: "heightFactor",
          type: "select",
          label: "view:download.nsdHeightFactor",
          helpText: "view:download.nsdHeightFactorHelp",
          options: HEIGHT_FACTOR_OPTIONS,
          initialValue: 0,
        },
        {
          name: "showDebug",
          type: "checkbox",
          label: "general:common.showDebugOutput",
          initialValue: false,
        },
      ],
      onSubmit: () => null,
      formValidator: FormValidator,
      submitButton: btnGo,
    });

    translateTedivoForm<IExportNsdOptions>({ tedivoForm, getTranslation });

    const advancedOptionsHolder = document.createElement("sl-details");
    advancedOptionsHolder.appendChild(tedivoForm.form);
    advancedOptionsHolder.summary = getTranslation("general:common.advancedOptions");
    advancedOptionsHolder.open = false;

    const disclaimerHolder = document.createElement("p");
    disclaimerHolder.innerHTML = getTranslation("view:download.nsdDisclaimer");

    dialog.appendChild(disclaimerHolder);
    dialog.appendChild(advancedOptionsHolder);
    dialog.appendChild(btnGo);

    // Resolve when the user closes with Escape or the X, so callers don't hang
    // waiting for a result that will never come.
    dialog.addEventListener("sl-request-close", () => {
      resolve(undefined);
      dialog.remove();
    });

    btnGo.addEventListener("click", () => {
      const validResult = tedivoForm.doSubmitForm();

      if (!validResult.success) return;

      dialog.hide();
      dialog.remove();

      resolve(validResult.data);
    });

    document.body.appendChild(dialog);
    dialog.show();
  });
}
